import React from 'react';
import { ArrowLeft, Search, HelpCircle, LogOut } from 'lucide-react';

interface GradesPageProps {
  onBack: () => void;
  onSelectAnotherTerm: () => void;
  term?: string;
}

const gradesByTerm: Record<string, { sidebarLabel: string; courses: Array<{
  format: string;
  subjectCode: string;
  subject: string;
  group: string;
  grade: string;
  absencesLimit: string;
  absences: string;
  noDeliverableLimit: string;
  extension: boolean;
}> }> = {
  'EC ABRIL 2026 (View only)': {
    sidebarLabel: 'Abr. 2026',
    courses: [
      {
        format: 'Flex',
        subjectCode: 'LTSO 2202',
        subject: 'Comunicación en equipos',
        group: '426',
        grade: '94',
        absencesLimit: '12',
        absences: '0',
        noDeliverableLimit: '1',
        extension: false
      },
      {
        format: 'Flex',
        subjectCode: 'LTPS 2204',
        subject: 'Gestión de calidad',
        group: '426',
        grade: '99',
        absencesLimit: '12',
        absences: '0',
        noDeliverableLimit: '1',
        extension: false
      },
      {
        format: 'Flex',
        subjectCode: 'LTHP 2206',
        subject: 'Análisis de datos II',
        group: '515',
        grade: '97',
        absencesLimit: '12',
        absences: '1',
        noDeliverableLimit: '1',
        extension: false
      },
      {
        format: 'Flex',
        subjectCode: 'LTMA 2210',
        subject: 'Producción y operaciones',
        group: '602',
        grade: '91',
        absencesLimit: '12',
        absences: '0',
        noDeliverableLimit: '1',
        extension: false
      },
      {
        format: 'Flex',
        subjectCode: 'LTTI 2204',
        subject: 'Industria y tecnología',
        group: '602',
        grade: '93',
        absencesLimit: '12',
        absences: '0',
        noDeliverableLimit: '1',
        extension: false
      },
      {
        format: 'Flex',
        subjectCode: 'LTTI 2208',
        subject: 'Seguridad en el trabajo',
        group: '602',
        grade: '100',
        absencesLimit: '12',
        absences: '0',
        noDeliverableLimit: '1',
        extension: false
      }
    ]
  },
  'EC SEPTIEMBRE 2025 (View only)': {
    sidebarLabel: 'Sep. 2025',
    courses: [
      {
        format: 'Flex',
        subjectCode: 'LTSO 2101',
        subject: 'Introducción a la Universidad',
        group: '125',
        grade: '100',
        absencesLimit: '12',
        absences: '0',
        noDeliverableLimit: '1',
        extension: false
      },
      {
        format: 'Flex',
        subjectCode: 'LTPS 2103',
        subject: 'Prin de bienestar y felicidad',
        group: '224',
        grade: '88',
        absencesLimit: '12',
        absences: '0',
        noDeliverableLimit: '1',
        extension: false
      },
      {
        format: 'Flex',
        subjectCode: 'LTHP 2104',
        subject: 'Formulacion y gestion de proyectos de ingenieria',
        group: '224',
        grade: '81',
        absencesLimit: '12',
        absences: '0',
        noDeliverableLimit: '1',
        extension: false
      },
      {
        format: 'Flex',
        subjectCode: 'LTMA 2108',
        subject: 'Calculo II',
        group: '321',
        grade: '94',
        absencesLimit: '12',
        absences: '0',
        noDeliverableLimit: '1',
        extension: false
      },
      {
        format: 'Flex',
        subjectCode: 'LTTI 2103',
        subject: 'Fisica II',
        group: '321',
        grade: '95',
        absencesLimit: '12',
        absences: '0',
        noDeliverableLimit: '1',
        extension: false
      }
    ]
  }
};

const GradesPage: React.FC<GradesPageProps> = ({ onBack, onSelectAnotherTerm, term }) => {
  const data = (term && gradesByTerm[term]) || gradesByTerm['EC ABRIL 2026 (View only)'];
  const courses = data.courses;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left - Logo */}
            <div className="flex items-center space-x-4">
              <img 
                src="/logo.png" 
                alt="TecMilenio Logo" 
                className="h-12 w-auto"
                style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(120deg) brightness(104%) contrast(97%)' }}
              />
              <span className="text-xl font-bold text-gray-800">TECMILENIO</span>
            </div>

            {/* Right - User Actions */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-green-500 text-white px-3 py-1 rounded">
                <LogOut size={16} />
                <span className="text-sm">Sign Out</span>
                <span className="text-sm">|</span>
                <HelpCircle size={16} />
                <span className="text-sm">Help</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Green Navigation Bar */}
      <div className="bg-green-500 text-white py-2">
        <div className="px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button className="bg-gray-600 text-white px-3 py-1 rounded text-sm">
                Browse
              </button>
              <span className="text-sm">Ayuda Financiera</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center bg-white rounded">
                <Search className="text-gray-400 ml-2" size={16} />
                <input 
                  type="text" 
                  placeholder="Find a page..." 
                  className="px-2 py-1 text-gray-800 text-sm rounded-r outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-6">
        <div className="flex justify-between items-start">
          {/* Left Content */}
          <div className="flex-1">
            {/* Back Button */}
            <button 
              onClick={onBack}
              className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
            >
              <ArrowLeft size={20} className="mr-1" />
            </button>

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-6">
              <span>Home</span>
              <span className="mx-2">&gt;</span>
              <span className="font-semibold text-gray-800">Grades</span>
            </nav>

            {/* Student Information */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Student Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <span className="text-sm text-gray-600">Program:</span>
                  <div className="font-medium">Ing Industrial y de Sist</div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Level:</span>
                  <div className="font-medium">Profesional</div>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Campus:</span>
                  <div className="font-medium">Campus Connect</div>
                </div>
              </div>
            </div>

            {/* Course Work Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b">
                <h2 className="text-xl font-bold text-gray-800">Course Work</h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Format</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject Code</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Absences Limit</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Absences</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No Deliverable Limit</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Extension</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {courses.map((course) => (
                      <tr key={course.subjectCode} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{course.format}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{course.subjectCode}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{course.subject}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{course.group}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{course.grade}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{course.absencesLimit}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{course.absences}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{course.noDeliverableLimit}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <input 
                            type="checkbox" 
                            checked={course.extension}
                            readOnly
                            className="rounded border-gray-300"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Instructional Text */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-700">
                Te recomendamos revisar el avance de tus actividades dando clic en la calificacion.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex space-x-4">
              <button 
                onClick={onBack}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded font-medium"
              >
                Back
              </button>
              <button 
                onClick={onSelectAnotherTerm}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded font-medium"
              >
                Select another Term
              </button>
            </div>
          </div>

          {/* Right Sidebar - User Info */}
          <div className="ml-8 text-right text-sm text-gray-600">
            <div className="font-medium">T07182039 Pablo Villarreal Lamberta</div>
            <div>{data.sidebarLabel}</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto">
        <div className="px-4 py-4 text-center">
          <div className="text-sm text-gray-600">
            SUNGARD HIGHER EDUCATION
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GradesPage;